/* global define */

define([
  'es6!sumo/util',
  'immutable',
  'moment',
  'moment-timezone',
  'es6!launcher/actions/AnalyticsActions',
], (Util, Immutable, moment, momentTz, Actions) => {
  // TODO: Maybe break this up by section, e.g.
  // forms, overview
  const { cacheGet, cacheSet, cacheGetSiteNs } = Util;

  const getCacheKey = (currentView, dateFilterInterval) => (
    `${cacheGetSiteNs()}:analytics:${currentView}:${dateFilterInterval}:state`
  );

  const getCachedState = (currentView, dateFilterInterval) => {
    const cacheKey = getCacheKey(currentView, dateFilterInterval);
    const cachedState = cacheGet(cacheKey);

    if (cachedState) {
      // bring data back from the dead
      cachedState.forms = Immutable.fromJS(cachedState.forms);
      cachedState.chartData = Immutable.fromJS(cachedState.chartData);
      cachedState.pageForms = Immutable.fromJS(cachedState.pageForms);

      return cachedState;
    }

    return {};
  };

  const cacheState = (state) => {
    const {
      currentView,
      dateFilterInterval,
      forms,
      chartData,
      totalPageViews,
      totalSessions,
      pageForms,
    } = state.toJS();
    const cacheKey = getCacheKey(currentView, dateFilterInterval);

    if (dateFilterInterval === 'custom') {
      return undefined;
    }

    // drop down to json so we can serialize
    return cacheSet(cacheKey, {
      forms,
      chartData,
      totalPageViews,
      totalSessions,
      pageForms,
    });
  };

  const dateFilterEnd = new Date();
  const dateFilterStart = moment(dateFilterEnd).startOf('day').subtract(6, 'days').toDate();
  const cachedState = getCachedState('overview', 'weekly');
  const initialState = Immutable.Map({
    dateFilterStart,
    dateFilterEnd,
    visitors: Immutable.Map(),
    forms: Immutable.List(),
    chartData: Immutable.Map(),
    totalPageViews: 0,
    totalSessions: 0,
    activePage: null,
    activeForm: null,
    isLoading: false,
    elasticSearchFailure: false,
    currentView: 'overview',
    pageForms: Immutable.Map(),
    dateFilterInterval: 'weekly', // [weekly, monthly, lifetime, custom]
    timeZone: momentTz.tz.guess(),
    scrollButtons: { forward: false, backward: true },
    shopProperties: Immutable.Map(),
    ...cachedState,
  });

  const actionHandlers = {};

  actionHandlers[Actions.RECEIVE_FORMS] = (state, action) => {
    const nextState = state.merge(Immutable.Map({
      isLoading: false,
      elasticSearchFailure: !action.data.success,
      forms: Immutable.fromJS(action.data.forms),
      chartData: Immutable.fromJS(action.data.chartData),
    }));

    // cache for next load
    if (action.data.success) {
      cacheState(nextState);
    }

    return nextState;
  };

  actionHandlers[Actions.REQUEST_OVERVIEW] = (state, action) => {
    return state.setIn(['isLoading'], true);
  };

  actionHandlers[Actions.RECEIVE_OVERVIEW] = (state, action) => {
    const siteUrl = state.get('siteUrl') || action.data.siteUrl;
    const nextState = state.merge(Immutable.Map({
      isLoading: false,
      elasticSearchFailure: !action.data.success,
      forms: Immutable.fromJS(action.data.forms),
      chartData: Immutable.fromJS(action.data.chartData),
      totalPageViews: action.data.totalPageViews,
      totalSessions: action.data.totalSessions,
      siteUrl,
      shopProperties: Immutable.fromJS(action.data.shopProperties),
      timestamp: action.data.timestamp,
    }));

    if (action.data.success) {
      cacheState(nextState);
    }

    return nextState;
  };

  actionHandlers[Actions.RECEIVE_FORM] = (state, action) => {
    const { data } = action;
    const { success, form } = data;

    return state.merge(Immutable.Map({
      isLoading: false,
      elasticSearchFailure: !success,
      activeForm: form,
    }));
  };

  actionHandlers[Actions.REQUEST_FORM] = (state, action) => {
    return state.setIn(['isLoading'], true);
  };

  actionHandlers[Actions.REQUEST_FORMS] = (state, action) => {
    return state.setIn(['isLoading'], true);
  };

  actionHandlers[Actions.SORT_FORMS] = (state, action) => {
    const { sortKey } = action;
    const forms = state.get('forms');

    const order = sortKey.startsWith('-') ? 1 : -1;
    const sortedForms = forms.sort((a, b) => {
      let sortVal = 0;

      if (sortKey.endsWith('updatedAt')) {
        sortVal = new Date(a.get('updatedAt')) - new Date(b.get('updatedAt'));
      } else if (sortKey.endsWith('name')) {
        sortVal = b.get('name').localeCompare(a.get('name'));
      } else if (sortKey.endsWith('best')) {
        sortVal = a.get('wilsonScore') - b.get('wilsonScore');
      } else {
        sortVal = a.get(sortKey.replace('-', '')) - b.get(sortKey.replace('-', ''));
      }

      return order * sortVal;
    });

    return state.merge(Immutable.Map({
      forms: sortedForms,
    }));
  };

  actionHandlers[Actions.UPDATE_ANALYTICS_SETTING] = (state, action) => {
    const settingName = action.setting;
    const settingValue = action.value;

    if (!settingName) return state;

    let nextState = state.setIn([settingName], settingValue);

    if (settingName === 'currentView' || settingName === 'dateFilterInterval') {
      const nextCachedState = getCachedState(nextState.get('currentView'), nextState.get('dateFilterInterval'));
      nextState = nextState.merge(nextCachedState);
    }

    return nextState;
  };

  return Util.createReducer(initialState, actionHandlers);
});

